import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import { FiFilter } from "react-icons/fi";
import { ADD_TASK, START, END } from "../../routes";
import { getDays, downloadTask } from "../../Services/TrackerServices";
import { getAllUser } from "../../Services/UserServices";
import { RESET_PAGINATION, ROLES, STATUS } from "../../Constants/Strings";
import { Spinner } from "../../Components/Common";
import { UserContext } from "../../Context";
import { formatDayDate } from "../../Utils/dateUtils";
import { DayCard, TaskMilestoneSection, DownloadCSVButton } from "./Components";
import { MESSAGES } from "./constants";
import { getTodayTasks } from "../../Services/TaskService";

const Home = () => {
  const [days, setDays] = useState([]);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ user: "", date: "" });
  const [tempFilters, setTempFilters] = useState({ user: "", date: "" });
  const [userOptions, setUserOptions] = useState([]);

  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const isToday = (dateString) => {
    const taskDate = new Date(dateString).toDateString();
    const today = new Date().toDateString();
    return taskDate === today;
  };

  const [todayDayData, setTodayDayData] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);

  const lastTask = todayTasks?.[todayTasks.length - 1];
  const isLastTaskIncomplete = lastTask?.status === STATUS.IN_PROGRESS;

  useEffect(() => {
    if (user?.role !== ROLES.ADMIN) return;
    getAllUser()
      .then((res) => {
        if (res.status === 200) {
          setUserOptions(res.data.results || []);
        }
      })
      .catch((err) => toast.error(MESSAGES.USER_FETCH_ERROR));
  }, []);

  useEffect(() => {
    getTodayTasks()
      .then((res) => {
        if (res.status === 200) {
          setTodayDayData(res.data[0]);
          setTodayTasks(res.data[0].tasks || []);
        }
      })
      .catch(() => {});
  }, []);

  const fetchDays = async (params, isInitial = false) => {
    if (!isInitial) setPaginationLoading(true);
    else setLoading(true);

    try {
      const queryParams = {
        ...params,
        ...(filters.user && { user: filters.user }),
        ...(filters.date && { date: filters.date }),
      };

      const response = await getDays(queryParams);

      if (response.status === 200) {
        const results = response.data.results || [];

        if (isInitial) {
          setDays(results);
        } else {
          setDays((prev) => [...prev, ...results]);
        }

        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: isInitial ? results.length : prev.offset + results.length,
        }));
      } else {
        toast.error(MESSAGES.DAY_LOAD_ERROR);
      }
    } catch (error) {
      toast.error(MESSAGES.DAY_LOAD_ERROR);
    } finally {
      setPaginationLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDays({ ...RESET_PAGINATION, ...tempFilters, ...filters }, true);
  }, [filters]);

  const inlineFilters = () => (
    <div className="hidden md:flex gap-4 items-end mb-4">
      {user?.role === ROLES.ADMIN && (
        <div>
          <label className="block text-sm text-primary mb-1">User</label>
          <select
            value={filters.user}
            onChange={(e) => {
              const value = e.target.value;
              const updatedFilters = { ...filters, user: value };
              setFilters(updatedFilters);
              setTempFilters(updatedFilters);
              setPagination(RESET_PAGINATION);
            }}
            className="border px-3 py-2 rounded"
          >
            <option value="">All Users</option>
            {userOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-col">
        <label className="block text-sm text-primary mb-1">Date</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.date}
            onChange={(e) => {
              const updated = { ...filters, date: e.target.value };
              setFilters(updated);
              setTempFilters(updated);
              setPagination(RESET_PAGINATION);
              fetchDays({ ...RESET_PAGINATION, ...updated }, true);
            }}
            className="border px-3 py-2 rounded"
          />

          {filters.date && (
            <button
              onClick={() => {
                const updated = { ...filters, date: "" };
                setFilters(updated);
                setTempFilters(updated);
                setPagination(RESET_PAGINATION);
                fetchDays({ ...RESET_PAGINATION, ...updated }, true);
              }}
              className="text-primary bg-blue-100 rounded-sm px-2 py-1 hover:text-red-500"
              title="Clear date"
              type="submit"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const loadMore = () => {
    if (pagination.offset >= pagination.count) return;
    fetchDays({ ...pagination }, false);
  };

  const handleApply = () => {
    setShowFilter(false);
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    fetchDays({ ...RESET_PAGINATION, ...tempFilters }, true);
  };

  const handleClear = () => {
    const cleared = { user: "", date: "" };
    setFilters(cleared);
    setTempFilters(cleared);
    setPagination(RESET_PAGINATION);
    fetchDays({ ...RESET_PAGINATION }, true);
  };

  const handleCancel = () => {
    setShowFilter(false);
    setTempFilters(filters);
  };

  useEffect(() => {
    fetchDays({ ...RESET_PAGINATION }, true);
  }, []);

  return (
    <div className="flex flex-col md:flex-row">
      <div className="w-full md:w-1/4 flex flex-col p-4 border border-r border-slate-100 bg-white min-h-[15vh] md:min-h-[60vh] relative">
        {!todayDayData && (
          <button
            className="bg-primary hover:bg-hoverColor text-white px-6 py-3 font-semibold"
            onClick={() => {
              navigate(START);
            }}
          >
            Start Day
          </button>
        )}

        {todayDayData?.status === STATUS.IN_PROGRESS && (
          <div className="flex flex-col items-center gap-2">
            <div className="text-md md:text-lg font-semibold text-primary">
              Day Started ({formatDayDate(todayDayData.created)})
            </div>
            <div className="w-full flex gap-2">
              <button
                disabled={isLastTaskIncomplete}
                className={`w-1/2 px-4 py-2 rounded-lg shadow
                    ${
                      isLastTaskIncomplete
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-primary hover:bg-hoverColor text-white"
                    }
                  `}
                onClick={() => navigate(`${ADD_TASK}?dayId=${todayDayData.id}`)}
              >
                {isLastTaskIncomplete ? "Complete Current Task" : "Add Task"}
              </button>
              <button
                disabled={isLastTaskIncomplete}
                className={`w-1/2 px-4 py-2 bg-primary text-white rounded-lg
                        ${
                          isLastTaskIncomplete
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-primary hover:bg-hoverColor text-white"
                        }
                      `}
                onClick={() => {
                  navigate(END.replace(":dayId", todayDayData.id));
                }}
              >
                End Day
              </button>
            </div>

            <div className="overflow-y-auto w-full overflow-x-hidden px-2">
              <TaskMilestoneSection tasks={todayTasks} showIcons={false} />
            </div>
          </div>
        )}

        {(todayDayData?.status === STATUS.COMPLETED || todayDayData?.status === STATUS.COMPLETED_BY_ADMIN) && (
          <div>
            <div className="flex justify-around text-md md:text-lg font-semibold text-primary">
              Day Completed ({formatDayDate(todayDayData.created)})
            </div>
            <TaskMilestoneSection tasks={todayTasks} showIcons={false} />
          </div>
        )}
      </div>

      {/* Right Hand Side */}
      <div className={"w-full md:w-3/4 p-4 relative"}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-primary">Past Days</h2>
          <div className="flex gap-3 items-center">
            <DownloadCSVButton
              fetchData={() =>
                downloadTask({
                  ...(filters.user && { user: filters.user }),
                  ...(filters.date && { date: filters.date }),
                })
              }
            />
            <button
              onClick={() => {
                setTempFilters(filters);
                setShowFilter(true);
              }}
              className="md:hidden px-4 py-2 rounded bg-primary hover:bg-hoverColor text-white font-bold shadow-lg flex items-center gap-1"
            >
              <FiFilter className="text-xl" />
            </button>
            {inlineFilters()}
          </div>
        </div>

        {showFilter && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="bg-white w-[90%] max-w-md rounded-xl p-6 shadow-lg">
              <h2 className="text-lg font-semibold mb-4 text-center text-primary">
                Filter
              </h2>

              {user?.role === ROLES.ADMIN && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 text-primary">
                    User
                  </label>
                  <select
                    value={tempFilters.user}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        user: e.target.value,
                      }))
                    }
                    className="w-full border px-3 py-2 rounded"
                  >
                    <option value="">All</option>
                    {userOptions.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm text-primary font-medium mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={tempFilters.date}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                  className="w-full border px-3 py-2 rounded"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleClear}
                  className="px-4 py-1 border border-black rounded"
                >
                  Clear
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-1 border border-black rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className="px-4 py-1 bg-primary text-white rounded"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
        {/* TODO: Optimize with below section */}
        {(loading || paginationLoading) && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
            <Spinner size={30} />
          </div>
        )}

        {!loading && days.length === 0 && (
          <div className="text-center text-secondary py-10">
            {MESSAGES.NO_HISTORY}
          </div>
        )}

        {!loading && days.length > 0 && (
          <InfiniteScroll
            dataLength={days.length}
            next={loadMore}
            hasMore={pagination.offset < pagination.count}
            scrollableTarget="scrollableContainer"
          >
            <div
              className="h-[60vh] overflow-x-auto overflow-y-auto"
              id="scrollableContainer"
            >
              <div className="flex flex-wrap">
                {days.map((day) => (
                  <DayCard key={day.id} data={day} />
                ))}
              </div>
            </div>
          </InfiniteScroll>
        )}

        {paginationLoading && (
          <div className="flex absolute top-0 left-0 h-full w-full justify-center items-center">
            <Spinner size={30} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
